import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { blogPosts } from "@/data/blogPosts";
import { Calendar, Clock } from "lucide-react";

const Blog = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "TodoTomorrow Blog",
    "description": "Thoughts on productivity, email workflows, and building TodoTomorrow",
    "url": "https://www.todotomorrow.com/blog",
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.description,
      "datePublished": post.date,
      "author": {
        "@type": "Person",
        "name": post.author
      },
      "url": `https://www.todotomorrow.com/blog/${post.slug}`
    }))
  };

  return (
    <>
      <Helmet>
        <title>Blog - TodoTomorrow | Productivity & Email Workflow Insights</title>
        <meta name="description" content="Thoughts on productivity, email workflows, and building TodoTomorrow - a mobile app to capture tasks on the go and have them delivered to your inbox." />
        <meta name="keywords" content="productivity blog, email workflows, task management, TodoTomorrow blog, productivity tips" />
        <link rel="canonical" href="https://www.todotomorrow.com/blog" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Blog - TodoTomorrow" />
        <meta property="og:description" content="Thoughts on productivity, email workflows, and building TodoTomorrow" />
        <meta property="og:url" content="https://www.todotomorrow.com/blog" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog - TodoTomorrow" />
        <meta name="twitter:description" content="Thoughts on productivity, email workflows, and building TodoTomorrow" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="min-h-screen pt-32 pb-24 px-6">
        <div className="container mx-auto max-w-4xl">
          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground mb-6">
              Blog
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Thoughts on productivity, email workflows, and building TodoTomorrow
            </p>
          </motion.header>

          {/* Blog Posts Grid */}
          <section className="space-y-6 sm:space-y-8">
            {blogPosts.map((post, index) => (
              <motion.article
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={`/blog/${post.slug}`} aria-label={`Read article: ${post.title}`}>
                  <Card className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <CardHeader>
                      <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-3 text-xs sm:text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                          <time dateTime={post.date}>
                            {new Date(post.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </time>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      <CardTitle className="text-xl sm:text-2xl mb-2 hover:text-primary transition-colors">
                        {post.title}
                      </CardTitle>
                      <CardDescription className="text-sm sm:text-base">
                        {post.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <span className="text-xs sm:text-sm text-muted-foreground">By {post.author}</span>
                        <Badge variant="secondary" className="text-xs sm:text-sm">Read more →</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.article>
            ))}
          </section>
        </div>
      </main>
    </>
  );
};

export default Blog;
